// Add Lender Dialog Component
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useLenders } from '../../hooks/useLenders';

interface AddLenderDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddLenderDialog: React.FC<AddLenderDialogProps> = ({ open, onClose, onSuccess }) => {
  const { addLender } = useLenders();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    authUserId: '', // NEW: Link to Firebase Auth user
    fullName: '',
    email: '',
    phone: '',
    address: '',
    idNumber: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    bankCode: '',
    branchCode: '',
  });
  
  const [linkToAuthUser, setLinkToAuthUser] = useState(false);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Valid email is required');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Create lender first
      const lenderIdResult = await addLender({
        fullName: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        idNumber: formData.idNumber.trim(),
        bankDetails: {
          bankName: formData.bankName.trim(),
          accountName: formData.accountName.trim(),
          accountNumber: formData.accountNumber.trim(),
          bankCode: formData.bankCode.trim(),
          branchCode: formData.branchCode.trim(),
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        totalInvested: 0,
      });
      
      // If Auth UID provided, create user document and link to lender
      if (formData.authUserId.trim()) {
        const { setDoc, doc, Timestamp } = await import('firebase/firestore');
        const { db } = await import('../../config/firebase');
        
        await setDoc(doc(db, 'users', formData.authUserId.trim()), {
          email: formData.email.trim().toLowerCase(),
          role: 'investor',
          fullName: formData.fullName.trim(),
          lenderId: lenderIdResult, // Link to the lender we just created
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        
        console.log('✅ Linked Auth user to lender:', formData.authUserId.trim());
      }

      // Reset form
      setFormData({
        authUserId: '',
        fullName: '',
        email: '',
        phone: '',
        address: '',
        idNumber: '',
        bankName: '',
        accountName: '',
        accountNumber: '',
        bankCode: '',
        branchCode: '',
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create lender');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          border: '1px solid #FFD700',
        }
      }}
    >
      <DialogTitle sx={{ color: '#FFD700', borderBottom: '1px solid #333' }}>
        Add New Lender
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#FFD700', mt: 1 }}>
            Personal Information
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="caption">
              💡 <strong>To link to Firebase Auth user:</strong><br/>
              1. Create Auth user in Firebase Console first<br/>
              2. Copy the User UID<br/>
              3. Paste it in "Auth User UID" field below (optional)<br/>
              This will create the user profile and link to this lender automatically.
            </Typography>
          </Alert>
          
          <TextField
            fullWidth
            label="Firebase Auth User UID (Optional)"
            value={formData.authUserId || ''}
            onChange={handleChange('authUserId')}
            placeholder="Paste UID from Firebase Console if linking existing Auth user"
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
            helperText="Leave empty if not linking to existing Auth user"
          />

          <TextField
            required
            fullWidth
            label="Full Name"
            value={formData.fullName}
            onChange={handleChange('fullName')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            required
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            required
            fullWidth
            label="Phone"
            value={formData.phone}
            onChange={handleChange('phone')}
            placeholder="+852 XXXX XXXX"
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            fullWidth
            label="Address"
            value={formData.address}
            onChange={handleChange('address')}
            multiline
            rows={2}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            fullWidth
            label="ID/Passport Number"
            value={formData.idNumber}
            onChange={handleChange('idNumber')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <Typography variant="subtitle2" sx={{ color: '#FFD700', mt: 2 }}>
            Bank Details
          </Typography>

          <TextField
            fullWidth
            label="Bank Name"
            value={formData.bankName}
            onChange={handleChange('bankName')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            fullWidth
            label="Account Name"
            value={formData.accountName}
            onChange={handleChange('accountName')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <TextField
            fullWidth
            label="Account Number"
            value={formData.accountNumber}
            onChange={handleChange('accountNumber')}
            disabled={loading}
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ style: { color: 'white' } }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Bank Code"
              value={formData.bankCode}
              onChange={handleChange('bankCode')}
              disabled={loading}
              InputLabelProps={{ style: { color: '#999' } }}
              InputProps={{ style: { color: 'white' } }}
            />

            <TextField
              fullWidth
              label="Branch Code"
              value={formData.branchCode}
              onChange={handleChange('branchCode')}
              disabled={loading}
              InputLabelProps={{ style: { color: '#999' } }}
              InputProps={{ style: { color: 'white' } }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{ color: 'white' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            bgcolor: '#FFD700',
            color: '#000',
            '&:hover': { bgcolor: '#FFC700' },
          }}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Lender'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLenderDialog;
